import { useEffect, useRef, useState } from 'react'
import { HardDrive, Pencil, Plus, Trash2 } from 'lucide-react'
import { Link, Outlet, useNavigate, useOutletContext, useParams } from 'react-router'

import { ConnectionTypes } from 'config/constants'
import { driveSettingDescriptors } from 'config/driveSettings'
import { useConfiguration, usePublishConfiguration, useUpdateConfiguration } from 'query/useConfigurations'
import {
  Alert,
  Button,
  ButtonLink,
  CheckboxField,
  ConfirmNameModal,
  DataTable,
  DriveLogo,
  EmptyState,
  Field,
  FormActions,
  FormCard,
  FormStack,
  Icon,
  IconButtonLink,
  Input,
  LoadingIndicator,
  PageContent,
  PageHeader,
  TabPane,
  Tabs,
} from 'ui'
import { Prompt } from 'utils/router'

import CopyableSpan from './CopyableSpan'
import formatLicenseKey from './formatLicenseKey'
import parseJSONField from './parseJSONField'

export const DeploymentsEdit = () => {
  const navigate = useNavigate()
  const { id: deploymentId } = useParams()
  const [drives, setDrives] = useState([])
  const [settings, setSettings] = useState({})
  const [licenseSettings, setLicenseSettings] = useState({})
  const [name, setName] = useState('')
  const [settingsDirty, setSettingsDirty] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [licenseKey, setLicenseKey] = useState('')
  const [users, setUsers] = useState([])
  const [settingsError, setSettingsError] = useState('')
  const [licenseSettingsError, setLicenseSettingsError] = useState('')
  const savedSettingsRef = useRef({})
  const savedLicenseSettingsRef = useRef({})

  const deploymentQuery = useConfiguration(deploymentId)
  const updateConfigurationMutation = useUpdateConfiguration(deploymentId, { onSuccess: () => setDirty(true) })
  const publishConfigurationMutation = usePublishConfiguration(deploymentId, {
    onSuccess: () => deploymentQuery.invalidateQuery(),
  })

  useEffect(() => {
    if (!deploymentQuery.data?.id) return
    setLicenseKey(deploymentQuery.data.key)
    setDirty(deploymentQuery.data.dirty)
    setName(deploymentQuery.data.name)
    setUsers(deploymentQuery.data.users || [])
    const nextSettings = parseJSONField(deploymentQuery.data.settings, {})
    const nextLicenseSettings = parseJSONField(deploymentQuery.data.licenseSettings, {})
    setSettings(nextSettings)
    setLicenseSettings(nextLicenseSettings)
    savedSettingsRef.current = nextSettings
    savedLicenseSettingsRef.current = nextLicenseSettings
    setDrives(parseJSONField(deploymentQuery.data.favorites, []))
    setSettingsDirty(false)
  }, [deploymentQuery.data])

  const handleChangeSettings = (event, field) => {
    setSettings(current => ({ ...current, [field]: event.target.value }))
    setSettingsDirty(true)
  }

  const persistSettings = nextSettings => {
    if (JSON.stringify(nextSettings) === JSON.stringify(savedSettingsRef.current)) return

    const previousSettings = savedSettingsRef.current
    setSettingsError('')
    updateConfigurationMutation.mutate(
      { settings: nextSettings },
      {
        onError: error => {
          setSettings(previousSettings)
          setSettingsError(error?.message || 'Failed to save configuration settings. Please try again.')
        },
        onSuccess: () => {
          savedSettingsRef.current = nextSettings
        },
      },
    )
  }

  const handleBlurSettings = () => persistSettings(settings)

  const handleToggleSettings = (event, field) => {
    const nextSettings = { ...settings, [field]: event.target.checked }
    setSettings(nextSettings)
    setSettingsDirty(true)
    persistSettings(nextSettings)
  }

  const handleChangeLicenseSettings = (event, field) => {
    const previousSettings = savedLicenseSettingsRef.current
    const next = { ...licenseSettings, [field]: event.target.checked }
    setLicenseSettings(next)
    setLicenseSettingsError('')
    setSettingsDirty(true)
    updateConfigurationMutation.mutate(
      { licenseSettings: next },
      {
        onError: error => {
          setLicenseSettings(previousSettings)
          setLicenseSettingsError(error?.message || 'Failed to save license settings. Please try again.')
        },
        onSuccess: () => {
          savedLicenseSettingsRef.current = next
        },
      },
    )
  }

  const publishDeployment = () => publishConfigurationMutation.mutate()

  return (
    <>
      <Prompt message="You have unpublished changes. Are you sure you want to leave?" when={settingsDirty} />
      <PageHeader
        action={(
          <ConfirmNameModal
            actionLabel="Delete"
            confirmationAction="delete this configuration"
            isPending={updateConfigurationMutation.isPending}
            name={name}
            onConfirm={async () => {
              await updateConfigurationMutation.mutateAsync({ status: 'trashed' })
              navigate('/configurations')
            }}
            title="Delete Configuration"
            trigger={<Button icon={<Icon as={Trash2} />} variant="danger">Delete Configuration</Button>}
          >
            Installed clients will become unlicensed, and this configuration and its licenses cannot be restored.
          </ConfirmNameModal>
        )}
        breadcrumbs={<><Link className="tw:text-foreground-inverse/65 tw:hover:text-foreground-inverse" to="/configurations">Configurations</Link><span className="tw:mx-3">/</span><span className="tw:text-foreground-inverse">{name}</span></>}
        helpHref="https://docs.expandrive.com/web-management-console#drive-settings"
        helpLabel="Help — Drive Settings"
        meta={(
          <span className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
            <span>License:</span>
            <CopyableSpan text={formatLicenseKey(licenseKey)} tone="inverse" />
            <span className="tw:h-1 tw:w-1 tw:rounded-full tw:bg-foreground-inverse/35" />
            <span><strong className="tw:text-foreground-inverse">{users.length}</strong> active users</span>
          </span>
        )}
        title={name || 'Configuration'}
      />
      <PageContent>
        {deploymentQuery.isLoading ? <LoadingIndicator /> : (
          <>
            <Tabs>
              <TabPane key="drives" tab="Drives" to={`/configurations/${deploymentId}`} />
              <TabPane key="app-settings" tab="App Settings" to={`/configurations/${deploymentId}/app-settings`} />
              <TabPane key="license-settings" tab="License Settings" to={`/configurations/${deploymentId}/license-settings`} />
            </Tabs>
            <Outlet
              context={{
                deploymentId,
                dirty,
                drives,
                handleChangeLicenseSettings,
                handleChangeSettings,
                handleBlurSettings,
                handleToggleSettings,
                licenseSettings,
                licenseSettingsError,
                publishDeployment,
                publishPending: publishConfigurationMutation.isPending,
                savePending: updateConfigurationMutation.isPending,
                settings,
                settingsError,
              }}
            />
          </>
        )}
      </PageContent>
    </>
  )
}

const useDeploymentsEditContext = () => useOutletContext()

export const DeploymentsEditDrivesTab = () => {
  const { deploymentId, dirty, drives, publishDeployment, publishPending } = useDeploymentsEditContext()
  const columns = [
    {
      key: 'name',
      label: 'Nickname',
      primary: true,
      render: drive => (
        <span className="tw:flex tw:items-center tw:gap-3">
          {ConnectionTypes[drive.type]?.brandingIcon && <DriveLogo src={ConnectionTypes[drive.type].brandingIcon} />}
          <span className="tw:font-semibold tw:text-foreground-strong">{drive.name || '(Unnamed)'}</span>
        </span>
      ),
    },
    { key: 'type', label: 'Drive Type', render: drive => ConnectionTypes[drive.type]?.name || drive.type },
    { key: 'mountpoint', label: 'Windows Drive Letter', render: drive => <strong className="tw:font-semibold">{drive.mountpoint || '—'}</strong> },
    { key: 'remotePath', label: 'Remote Path', span: true, render: drive => <span className="ds-mono">{drive.remotePath || '/'}</span> },
  ]

  return (
    <>
      <DataTable
        actions={drive => (
          <IconButtonLink
            label={`Edit ${drive.name || 'drive'}`}
            title="Edit Drive"
            to={`/configurations/${deploymentId}/drives/${drive.id}`}
          >
            <Icon as={Pencil} />
          </IconButtonLink>
        )}
        columns={columns}
        empty={(
          <EmptyState
            action={<ButtonLink icon={<Icon as={Plus} />} to={`/configurations/${deploymentId}/drives/new`}>New Drive</ButtonLink>}
            icon={<Icon as={HardDrive} size={32} />}
            title="No Drives Yet"
          >
            Add a drive to map a remote connection for this configuration.
          </EmptyState>
        )}
        rows={drives}
      />
      {drives.length > 0 && (
        <FormActions className="tw:mt-6">
          <ButtonLink icon={<Icon as={Plus} />} to={`/configurations/${deploymentId}/drives/new`}>New Drive</ButtonLink>
          <Button disabled={!dirty || publishPending} onClick={publishDeployment} variant="secondary">
            {publishPending ? 'Publishing…' : 'Publish Configuration'}
          </Button>
        </FormActions>
      )}
    </>
  )
}

export const DeploymentsEditAppSettingsTab = () => {
  const {
    dirty,
    handleBlurSettings,
    handleChangeSettings,
    handleToggleSettings,
    publishDeployment,
    publishPending,
    savePending,
    settings,
    settingsError,
  } = useDeploymentsEditContext()

  return (
    <div className="tw:mx-auto tw:max-w-form">
      <FormStack>
        {settingsError && <Alert color="danger">{settingsError}</Alert>}
        <FormCard description="Control how ExpanDrive starts and behaves on user devices." title="Application Behavior">
          {driveSettingDescriptors.map(({ field, helper, label }) => (
            <CheckboxField
              checked={Boolean(settings[field])}
              helper={helper}
              key={field}
              label={label}
              onChange={event => handleToggleSettings(event, field)}
            />
          ))}
        </FormCard>
        <FormCard description="Set a domain hint for enterprise authentication." title="SSO Configuration">
          <Field helper="Users authenticate through your organization’s SSO provider." label="Domain Hint">
            <Input name="domain_hint" onBlur={handleBlurSettings} onChange={event => handleChangeSettings(event, 'domain_hint')} placeholder="example.com" value={settings.domain_hint || ''} />
          </Field>
        </FormCard>
        <FormCard description="Choose the base directory used to mount drives on Linux systems." title="Linux Mount Location">
          <Field helper="All drives mount under this path." label="Root Mount Path">
            <Input className="ds-mono" name="mountLocation" onBlur={handleBlurSettings} onChange={event => handleChangeSettings(event, 'mountLocation')} placeholder="/mnt/expandrive" value={settings.mountLocation || ''} />
          </Field>
        </FormCard>
      </FormStack>
      <FormActions className="tw:mt-5">
        <Button disabled={!dirty || publishPending || savePending} onClick={publishDeployment}>
          {publishPending ? 'Publishing…' : 'Publish Configuration'}
        </Button>
      </FormActions>
    </div>
  )
}

export const DeploymentsEditLicenseSettingsTab = () => {
  const { dirty, handleChangeLicenseSettings, licenseSettings, licenseSettingsError, publishDeployment, publishPending } = useDeploymentsEditContext()

  return (
    <div className="tw:mx-auto tw:max-w-form">
      <FormCard description="Lock down what users can change after this configuration is published." title="License">
        {licenseSettingsError && <Alert className="tw:mb-4" color="danger">{licenseSettingsError}</Alert>}
        <CheckboxField
          checked={Boolean(licenseSettings.managedConnections)}
          helper="Users cannot add, edit, or remove drives outside this configuration."
          label="Managed Connections"
          onChange={event => handleChangeLicenseSettings(event, 'managedConnections')}
        />
        <CheckboxField
          checked={Boolean(licenseSettings.managedSettings)}
          helper="Users cannot change the application settings published here."
          label="Managed Settings"
          onChange={event => handleChangeLicenseSettings(event, 'managedSettings')}
        />
      </FormCard>
      <FormActions className="tw:mt-5">
        <Button disabled={!dirty || publishPending} onClick={publishDeployment}>
          {publishPending ? 'Publishing…' : 'Publish Configuration'}
        </Button>
      </FormActions>
    </div>
  )
}
